// Import from the source module rather than the `@/services/api` barrel: this
// file is pulled into the api-barrel <-> services/auth import cycle (via
// auth/hooks), and the barrel exports `hydraUiApi` after `servicesApi`, so going
// through the barrel can observe it before it's initialized.
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { crossAppCache } from '@/utils/cross-app-cache';
import type { RawAccount, RawUser, Workspace } from './types';

/**
 * Account reads for the auth/session bootstrap, routed through
 * `hydraUiApi.accounts.*` while preserving the cross-app cache that the
 * `@customerio/hydra-authentication` session singleton seeds on boot and that
 * other embedded apps (Journeys UI, Pipelines) read.
 *
 * The current-account fetch hits the `/v1/accounts/:account_id` route with the
 * literal `account_id` "current"; the backend resolves it to the session's
 * account.
 */

const ACCOUNT_CACHE_TTL = 10000;

export interface AccountResponse {
  account: RawAccount;
  environments: Workspace[];
  user: RawUser;
  session_expires_at: number;
}

// The generated `accountResponse` is the same backend payload as the old
// ts-rest contract, so narrow it back to the hand-written account shape the
// auth mappers consume.
function toAccountResponse(data: Awaited<ReturnType<typeof hydraUiApi.accounts.get>>): AccountResponse {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return data as unknown as AccountResponse;
}

/**
 * Seed the current-account cross-app cache entries from a fetched payload.
 * `cacheOnlyOnce` means the first write wins for the TTL window — callers that
 * seed manually (the signup readiness wait) must only pass a payload they want
 * the auth bootstrap and the embedded apps to reuse.
 */
export function seedCurrentAccountCache(response: AccountResponse): void {
  if (response.account?.id == null) {
    return;
  }
  crossAppCache.setValue({ key: '/accounts/current', value: response, ttl: ACCOUNT_CACHE_TTL, cacheOnlyOnce: true });
  crossAppCache.setValue({
    key: `/accounts/${response.account.id}`,
    value: response,
    ttl: ACCOUNT_CACHE_TTL,
    cacheOnlyOnce: true,
  });
}

/**
 * Fetch the current account with no cache read *and no cache write*. For
 * callers polling for a state change (the signup readiness wait): reading the
 * cache would freeze the first response for the TTL window, and writing would
 * let a not-yet-ready payload win the `cacheOnlyOnce` slot. Seed explicitly
 * via `seedCurrentAccountCache` once the payload is the one to keep.
 */
export async function fetchCurrentAccountUncached(signal?: AbortSignal): Promise<AccountResponse> {
  return toAccountResponse(await hydraUiApi.accounts.get({ params: { path: { account_id: 'current' } }, signal }));
}

/** Fetch the session's current account, seeding the cross-app cache like the old `servicesApi.getAccount()`. */
export async function fetchCurrentAccount(): Promise<AccountResponse> {
  const cached = crossAppCache.getValue('/accounts/current');
  if (cached != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return cached as AccountResponse;
  }

  const response = await fetchCurrentAccountUncached();
  seedCurrentAccountCache(response);
  return response;
}

/** Fetch an account by ID, mirroring the cross-app cache behavior of the old `servicesApi.getAccountById()`. */
export async function fetchAccountById(accountId: number): Promise<AccountResponse> {
  const cached = crossAppCache.getValue(`/accounts/${accountId}`);
  if (cached != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return cached as AccountResponse;
  }

  const response = toAccountResponse(
    await hydraUiApi.accounts.get({ params: { path: { account_id: String(accountId) } } }),
  );

  if (response.account?.id != null) {
    crossAppCache.setValue({
      key: `/accounts/${accountId}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
  }

  return response;
}

/** Fetch an account by environment ID, mirroring the old `servicesApi.getAccountByEnvironmentId()`. */
export async function fetchAccountByEnvironment(environmentId: number): Promise<AccountResponse> {
  const cached = crossAppCache.getValue(`/account_by_environment/${environmentId}`);
  if (cached != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return cached as AccountResponse;
  }

  const response = toAccountResponse(
    await hydraUiApi.accounts.getAccountByEnvironment({ params: { path: { environment_id: String(environmentId) } } }),
  );

  if (response.account?.id != null) {
    crossAppCache.setValue({
      key: `/account_by_environment/${environmentId}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
    crossAppCache.setValue({
      key: `/accounts/${response.account.id}`,
      value: response,
      ttl: ACCOUNT_CACHE_TTL,
      cacheOnlyOnce: true,
    });
  }

  return response;
}
